import * as pc from "playcanvas";
import { getWeapon } from "@hupper/shared/weapons";
import type { GameClient } from "../net/GameClient";
import { buildWeaponModel, type WeaponBuildRefs } from "./weaponBuilder";

/**
 * Live movement state read by the weapon view bob curves. Wired in
 * main.ts to `FirstPersonController.getMovementState()`.
 */
export interface MovementProbe {
  getMovementState(): { moving: boolean; walking: boolean; crouching: boolean; airborne: boolean };
}

/**
 * First-person weapon view-model — fully procedural using PlayCanvas
 * primitives. Each equipped class (pistol/smg/rifle/sniper/grenade/melee)
 * builds a distinct silhouette from boxes/cylinders/capsules.
 *
 * Sprint 5 adds: bob state machine (idle/walk/run/crouch), F-key
 * inspect animation, and an `isBusy()` flag so the FirstPersonController
 * can lock LMB during switch / inspect.
 */

export class LocalWeaponView {
  private readonly app: pc.AppBase;
  private readonly root: pc.Entity;
  private readonly client: GameClient;
  private lastWeaponId = "";
  private nextWeaponId = "";
  private isSwitching = false;
  private switchPhase = 0;
  private hasSwappedMesh = false;
  private readonly SWITCH_DURATION = 0.15; // 150ms down, 150ms up

  private phase = 0;
  private readonly unsubs: Array<() => void> = [];
  /** Muzzle flash entity (created lazily, lives across rebuilds). */
  private muzzleFlash: pc.Entity | null = null;
  /** Inner bright core. */
  private muzzleCore: pc.Entity | null = null;
  /** Outer soft glow puff. */
  private muzzlePuff: pc.Entity | null = null;
  /** Wall-clock ms when the flash started (for animation curve). */
  private muzzleFlashStart = 0;
  /** Total flash lifetime in ms. */
  private readonly MUZZLE_DURATION_MS = 80;
  /** Throttle muzzle visuals by RPM independently of input frequency. */
  private lastMuzzleMs = 0;

  // Kickback animation state
  private kickbackOffset = 0;
  private kickbackRotX = 0;
  private kickbackRotZ = 0;
  private kickbackPhase = 0;
  private kickbackDuration = 0.15; // 150ms
  private isKickingBack = false;
  private currentKickbackProfile = { offset: 0.05, rotX: 5, rotZ: 2 };

  // Knife attack animation state
  private isKnifeAttacking = false;
  private knifeAttackPhase = 0;
  private knifeAttackType: 'slash' | 'stab' = 'slash';
  private readonly KNIFE_SLASH_DURATION = 0.3;
  private readonly KNIFE_STAB_DURATION = 0.6;

  // Reload animation state
  private isReloading = false;
  private reloadPhase = 0;
  private reloadDuration = 2.0; // Will be set by weapon spec
  private magEntity: pc.Entity | null = null;
  private slideEntity: pc.Entity | null = null;
  private magOriginalPos = new pc.Vec3();
  private slideOriginalPos = new pc.Vec3();

  // Molotov lighting animation state
  private isLightingMolotov = false;
  private lightMolotovPhase = 0;
  private lighterEntity: pc.Entity | null = null;
  private lighterLidEntity: pc.Entity | null = null;
  private molotovFlameEntities: pc.Entity[] = [];
  private readonly LIGHT_MOLOTOV_DURATION = 1.2;

  /**
   * Sprint 5 — inspect animation (F key). Rotates the weapon left + tilts
   * up over INSPECT_DURATION, then returns to neutral. Holds player
   * control input; if a SHOOT happens it's cancelled.
   */
  private isInspecting = false;
  private inspectPhase = 0;
  private static readonly INSPECT_DURATION = 1.5;

  private grenadeChargePhase = 0;

  /**
   * Sprint 5 — bob state machine. Tracks current movement state so the
   * idle/walk/run bob curves can blend. Exposed through MovementProbe.
   */
  private movementProbe: MovementProbe | null = null;
  
  /** Probe to read grenade charging state. */
  private grenadeProbe: (() => { charging: boolean; amount: number }) | null = null;

  /**
   * Returns true while the local player is scoped (AWP) — the viewmodel
   * hides entirely, like CS:GO. Wired in main.ts to
   * `FirstPersonController.isScoped`.
   */
  private scopeProbe: (() => boolean) | null = null;

  private readonly onAppUpdate = (_dt: number) => {
    // Hide the first-person viewmodel while dead/spectating — otherwise the
    // knife (or current weapon) floats in front of the spectated player's
    // view. Bail before any animation work so nothing renders.
    const self = this.client.getLatestSelf();
    const alive = (self?.health ?? 1) > 0;
    // Scoped (AWP) also hides the viewmodel — animations keep advancing
    // underneath so a reload/switch in flight isn't frozen mid-pose when
    // the scope drops.
    const show = alive && !this.scopeProbe?.();
    if (this.root.enabled !== show) this.root.enabled = show;
    if (!alive) return;

    this.phase += _dt;

    let switchOffset = 0;
    let switchRotX = 0;
    let switchRotZ = 0;

    if (this.isSwitching) {
      this.switchPhase += _dt;
      if (this.switchPhase < this.SWITCH_DURATION) {
        // Going down
        const t = this.switchPhase / this.SWITCH_DURATION;
        const ease = t * t; // Ease in
        switchOffset = -0.5 * ease;
        switchRotX = 45 * ease;
        switchRotZ = -20 * ease;
      } else {
        if (!this.hasSwappedMesh) {
          this.lastWeaponId = this.nextWeaponId;
          this.rebuild(this.nextWeaponId);
          this.hasSwappedMesh = true;
        }
        
        if (this.switchPhase < this.SWITCH_DURATION * 2) {
          // Coming up
          const t = (this.switchPhase - this.SWITCH_DURATION) / this.SWITCH_DURATION;
          const ease = 1 - (1 - t) * (1 - t); // Ease out
          switchOffset = -0.5 * (1 - ease);
          switchRotX = 45 * (1 - ease);
          switchRotZ = -20 * (1 - ease);
        } else {
          this.isSwitching = false;
          this.switchPhase = 0;
        }
      }
    }

    // Kickback animation
    let kickOffset = 0;
    let kickRotX = 0;
    let kickRotZ = 0;

    if (this.isKickingBack) {
      this.kickbackPhase += _dt;
      if (this.kickbackPhase < this.kickbackDuration) {
        const t = this.kickbackPhase / this.kickbackDuration;
        // Fast attack, slow decay
        let ease = 0;
        if (t < 0.2) {
          ease = t / 0.2; // 0 to 1
        } else {
          ease = 1 - ((t - 0.2) / 0.8); // 1 to 0
        }
        
        kickOffset = this.currentKickbackProfile.offset * ease;
        kickRotX = this.currentKickbackProfile.rotX * ease;
        kickRotZ = this.currentKickbackProfile.rotZ * ease;
      } else {
        this.isKickingBack = false;
      }
    }

    // Knife attack animation
    let knifeOffsetX = 0;
    let knifeOffsetY = 0;
    let knifeOffsetZ = 0;
    let knifeRotX = 0;
    let knifeRotY = 0;
    let knifeRotZ = 0;

    if (this.isKnifeAttacking) {
      this.knifeAttackPhase += _dt;
      const duration = this.knifeAttackType === 'slash' ? this.KNIFE_SLASH_DURATION : this.KNIFE_STAB_DURATION;
      
      if (this.knifeAttackPhase < duration) {
        const t = this.knifeAttackPhase / duration;
        
        if (this.knifeAttackType === 'slash') {
          // Slash: Arc R→L over 200ms
          // Fast swing, slower recover
          let ease = 0;
          if (t < 0.3) {
            ease = t / 0.3; // 0 to 1 (swing)
          } else {
            ease = 1 - ((t - 0.3) / 0.7); // 1 to 0 (recover)
          }
          
          knifeOffsetX = -0.3 * ease; // move left
          knifeOffsetY = -0.1 * ease; // move slightly down
          knifeOffsetZ = -0.1 * ease; // move slightly forward
          knifeRotY = 45 * ease;      // rotate left
          knifeRotZ = -30 * ease;     // tilt
        } else {
          // Stab: Forward thrust with wind-up bob backward
          if (t < 0.3) {
            // Wind-up
            const ease = t / 0.3;
            knifeOffsetZ = 0.1 * ease; // pull back
            knifeRotX = -15 * ease;    // tilt up
          } else if (t < 0.5) {
            // Thrust
            const ease = (t - 0.3) / 0.2;
            knifeOffsetZ = 0.1 - (0.4 * ease); // thrust forward
            knifeRotX = -15 + (25 * ease);     // tilt down
          } else {
            // Recover
            const ease = 1 - ((t - 0.5) / 0.5);
            knifeOffsetZ = -0.3 * ease;
            knifeRotX = 10 * ease;
          }
        }
      } else {
        this.isKnifeAttacking = false;
      }
    }

    // Reload animation
    let reloadOffsetX = 0;
    let reloadOffsetY = 0;
    let reloadOffsetZ = 0;
    let reloadRotX = 0;
    let reloadRotY = 0;
    let reloadRotZ = 0;

    if (this.isReloading) {
      this.reloadPhase += _dt;
      if (this.reloadPhase < this.reloadDuration) {
        const t = this.reloadPhase / this.reloadDuration;
        const isMac10 = this.lastWeaponId === "mac10";
        const isMp9 = this.lastWeaponId === "mp9";
        
        // General weapon movement during reload
        if (isMac10) {
          // MAC-10 specific reload movement: tilt weapon to the side to expose bottom and top
          if (t < 0.15) {
            const ease = t / 0.15;
            reloadRotX = 20 * ease;
            reloadRotZ = 25 * ease;  // Tilt sideways
            reloadOffsetY = -0.05 * ease;
          } else if (t < 0.7) {
            reloadRotX = 20;
            reloadRotZ = 25;
            reloadOffsetY = -0.05;
          } else {
            const ease = 1 - ((t - 0.7) / 0.3);
            reloadRotX = 20 * ease;
            reloadRotZ = 25 * ease;
            reloadOffsetY = -0.05 * ease;
          }
        } else if (isMp9) {
          // MP9 specific reload movement: tilt up and slightly right to expose magwell inside grip
          if (t < 0.1) {
            const ease = t / 0.1;
            reloadRotX = 25 * ease;
            reloadRotY = 5 * ease;
            reloadRotZ = 5 * ease;
            reloadOffsetY = -0.02 * ease;
          } else if (t < 0.75) {
            reloadRotX = 25;
            reloadRotY = 5;
            reloadRotZ = 5;
            reloadOffsetY = -0.02;
          } else {
            const ease = 1 - ((t - 0.75) / 0.25);
            reloadRotX = 25 * ease;
            reloadRotY = 5 * ease;
            reloadRotZ = 5 * ease;
            reloadOffsetY = -0.02 * ease;
          }
        } else {
          if (t < 0.1) {
            // Tilt weapon up and left
            const ease = t / 0.1;
            reloadRotX = 15 * ease;
            reloadRotZ = 10 * ease;
            reloadOffsetY = -0.05 * ease;
          } else if (t < 0.8) {
            // Hold position
            reloadRotX = 15;
            reloadRotZ = 10;
            reloadOffsetY = -0.05;
          } else {
            // Return to normal
            const ease = 1 - ((t - 0.8) / 0.2);
            reloadRotX = 15 * ease;
            reloadRotZ = 10 * ease;
            reloadOffsetY = -0.05 * ease;
          }
        }

        // Mag animation
        if (this.magEntity) {
          const magPos = this.magOriginalPos.clone();
          if (isMac10) {
            if (t >= 0.15 && t < 0.35) {
              // Mag drops straight down
              const ease = (t - 0.15) / 0.2;
              magPos.y -= 0.3 * ease;
            } else if (t >= 0.35 && t < 0.5) {
              magPos.y -= 0.5;
            } else if (t >= 0.5 && t < 0.65) {
              // New mag comes up
              const ease = (t - 0.5) / 0.15;
              magPos.y -= 0.5 * (1 - ease);
            }
          } else if (isMp9) {
            if (t >= 0.1 && t < 0.3) {
              // Mag drops down from the grip
              const ease = (t - 0.1) / 0.2;
              magPos.y -= 0.25 * ease;
            } else if (t >= 0.3 && t < 0.45) {
              magPos.y -= 0.5;
            } else if (t >= 0.45 && t < 0.6) {
              // New mag comes up
              const ease = (t - 0.45) / 0.15;
              magPos.y -= 0.5 * (1 - ease);
            }
          } else {
            if (t >= 0.1 && t < 0.3) {
              // Mag drops
              const ease = (t - 0.1) / 0.2;
              magPos.y -= 0.2 * ease;
            } else if (t >= 0.3 && t < 0.5) {
              // Mag is gone (hide it out of view)
              magPos.y -= 0.5;
            } else if (t >= 0.5 && t < 0.6) {
              // New mag comes up
              const ease = (t - 0.5) / 0.1;
              magPos.y -= 0.5 * (1 - ease);
            }
          }
          this.magEntity.setLocalPosition(magPos);
        }

        // Slide animation (rack)
        if (this.slideEntity) {
          const slidePos = this.slideOriginalPos.clone();
          let slideRotZ = 0;
          const isAwp = (this.lastWeaponId === "awp" || this.lastWeaponId === "sniper");

          if (isAwp) {
            // AWP bolt action
            if (t >= 0.6 && t < 0.65) {
              const ease = (t - 0.6) / 0.05;
              slideRotZ = 60 * ease; // Lift up
            } else if (t >= 0.65 && t < 0.75) {
              slideRotZ = 60;
              const ease = (t - 0.65) / 0.1;
              slidePos.z += 0.08 * ease; // Pull back
            } else if (t >= 0.75 && t < 0.85) {
              slideRotZ = 60;
              const ease = 1 - ((t - 0.75) / 0.1);
              slidePos.z += 0.08 * ease; // Push forward
            } else if (t >= 0.85 && t < 0.9) {
              const ease = 1 - ((t - 0.85) / 0.05);
              slideRotZ = 60 * ease; // Push down
            }
          } else if (isMac10) {
            // MAC-10 charging handle pull (pull back, let go)
            if (t >= 0.7 && t < 0.8) {
              // Pull back
              const ease = (t - 0.7) / 0.1;
              slidePos.z += 0.08 * ease;
            } else if (t >= 0.8 && t < 0.85) {
              // Snap forward
              const ease = 1 - ((t - 0.8) / 0.05);
              slidePos.z += 0.08 * ease;
            }
          } else if (isMp9) {
            // MP9 charging handle pull (pull back, let go)
            if (t >= 0.65 && t < 0.72) {
              // Pull back
              const ease = (t - 0.65) / 0.07;
              slidePos.z += 0.06 * ease;
            } else if (t >= 0.72 && t < 0.76) {
              // Snap forward
              const ease = 1 - ((t - 0.72) / 0.04);
              slidePos.z += 0.06 * ease;
            }
          } else {
            // normal slide
            if (t >= 0.65 && t < 0.7) {
              // Pull back
              const ease = (t - 0.65) / 0.05;
              slidePos.z += 0.05 * ease;
            } else if (t >= 0.7 && t < 0.75) {
              // Snap forward
              const ease = 1 - ((t - 0.7) / 0.05);
              slidePos.z += 0.05 * ease;
            }
          }

          this.slideEntity.setLocalPosition(slidePos);
          if (isAwp) {
            this.slideEntity.setLocalEulerAngles(0, 0, slideRotZ);
          }
        }
      }
    }

    // Molotov lighting animation
    let molotovRotX = 0;
    let molotovRotZ = 0;
    if (this.isLightingMolotov) {
      this.lightMolotovPhase += _dt;
      const t = this.lightMolotovPhase / this.LIGHT_MOLOTOV_DURATION;

      // The molotov bottle tilts slightly towards the lighter
      if (t < 0.2) {
        const ease = t / 0.2;
        molotovRotX = -10 * ease;
        molotovRotZ = 15 * ease;
      } else if (t < 0.7) {
        molotovRotX = -10;
        molotovRotZ = 15;
      } else if (t < 0.9) {
        const ease = 1 - ((t - 0.7) / 0.2);
        molotovRotX = -10 * ease;
        molotovRotZ = 15 * ease;
      }

      if (this.lighterEntity && this.lighterLidEntity) {
        if (t < 0.2) {
          // Bring lighter up and to the right (towards the wick)
          const ease = Math.sin((t / 0.2) * Math.PI / 2); // easeOutSine
          this.lighterEntity.setLocalPosition(
            -0.3 + 0.22 * ease,
            -0.2 + 0.40 * ease,
            0.1 - 0.1 * ease
          );
          this.lighterEntity.setLocalEulerAngles(0, 0, 15 * ease);
          this.lighterLidEntity.setLocalEulerAngles(0, 0, 0); // lid closed
        } else if (t < 0.4) {
          // Open lid with a snap
          const ease = (t - 0.2) / 0.2;
          this.lighterLidEntity.setLocalEulerAngles(0, 0, -120 * ease);
          
          // Small shake from flicking the wheel
          if (t > 0.3 && t < 0.35) {
            this.lighterEntity.setLocalPosition(-0.08, 0.18, 0);
          } else {
            this.lighterEntity.setLocalPosition(-0.08, 0.20, 0);
          }
        } else if (t < 0.7) {
          // Flame is on!
          this.lighterLidEntity.setLocalEulerAngles(0, 0, -120);
          this.lighterEntity.setLocalPosition(-0.08, 0.20, 0);
          
          if (!this.molotovFlameEntities[0]?.enabled) {
            for (const f of this.molotovFlameEntities) f.enabled = true;
          }
        } else if (t < 0.9) {
          // Snap lid closed, pull lighter away
          const ease = (t - 0.7) / 0.2;
          this.lighterLidEntity.setLocalEulerAngles(0, 0, -120 * (1 - ease));
          this.lighterEntity.setLocalPosition(
            -0.08 - 0.22 * ease,
            0.20 - 0.40 * ease,
            0.1 * ease
          );
          this.lighterEntity.setLocalEulerAngles(0, 0, 15 * (1 - ease));
        } else {
          // Animation complete, lighter stays hidden
          this.lighterEntity.enabled = false;
          this.isLightingMolotov = false;
        }
      }
    } else {
      // Ensure flames stay on after lighting
      if (this.lastWeaponId === "molotov" && this.molotovFlameEntities.length > 0 && !this.molotovFlameEntities[0]?.enabled) {
         for (const f of this.molotovFlameEntities) f.enabled = true;
      }
    }

    // Sprint 5 — inspect animation (F key). Rotates the weapon left
    // around the player's view axis and tilts the muzzle up, hold for
    // a beat, then return to neutral. Cancelled if a knife/switch/reload
    // anim takes priority (they overwrite the offsets after this).
    let inspectOffsetX = 0;
    let inspectOffsetY = 0;
    let inspectOffsetZ = 0;
    let inspectRotX = 0;
    let inspectRotY = 0;
    let inspectRotZ = 0;
    if (this.isInspecting) {
      this.inspectPhase += _dt;
      const dur = LocalWeaponView.INSPECT_DURATION;
      if (this.inspectPhase < dur) {
        const t = this.inspectPhase / dur;
        // Three-phase: bring up (0-0.25), hold (0.25-0.75), return (0.75-1).
        let ease = 0;
        if (t < 0.25) ease = t / 0.25;
        else if (t < 0.75) ease = 1;
        else ease = 1 - (t - 0.75) / 0.25;
        inspectOffsetX = -0.05 * ease;
        inspectOffsetY = 0.04 * ease;
        inspectOffsetZ = -0.05 * ease;
        inspectRotY = -55 * ease;
        inspectRotX = -25 * ease;
        inspectRotZ = 10 * ease;
      } else {
        this.isInspecting = false;
        this.inspectPhase = 0;
      }
    }

    // Grenade charge / pull-pin animation
    const chargeState = this.grenadeProbe?.();
    const isGrenade = this.lastWeaponId === "he" || this.lastWeaponId === "flash" || this.lastWeaponId === "smoke";
    if (chargeState?.charging && isGrenade) {
      this.grenadeChargePhase += _dt;
    } else {
      this.grenadeChargePhase = 0;
    }

    let chargeOffsetX = 0;
    let chargeOffsetY = 0;
    let chargeOffsetZ = 0;
    let chargeRotX = 0;
    let chargeRotY = 0;
    let chargeRotZ = 0;

    if (this.grenadeChargePhase > 0) {
      if (this.grenadeChargePhase < 0.15) {
        // bring grenade closer and tilt to expose pin
        const t = this.grenadeChargePhase / 0.15;
        chargeOffsetX = -0.05 * t;
        chargeOffsetY = 0.02 * t;
        chargeRotZ = -15 * t; 
        chargeRotY = 20 * t;
      } else if (this.grenadeChargePhase < 0.25) {
        // pull pin (jerk down)
        const t = (this.grenadeChargePhase - 0.15) / 0.1;
        chargeOffsetX = -0.05;
        chargeOffsetY = 0.02 - 0.04 * t; 
        chargeRotZ = -15 + 5 * t;
        chargeRotY = 20;
      } else if (this.grenadeChargePhase < 0.4) {
        // cock back to throw position
        const t = (this.grenadeChargePhase - 0.25) / 0.15;
        chargeOffsetX = -0.05 + 0.1 * t; // move right
        chargeOffsetY = -0.02 - 0.03 * t; // drop down slightly
        chargeOffsetZ = 0.1 * t; // pull back
        chargeRotZ = -10 + 10 * t;
        chargeRotY = 20 - 40 * t;
        chargeRotX = -20 * t; // tilt up for throw
      } else {
        // hold in throw position
        chargeOffsetX = 0.05;
        chargeOffsetY = -0.05;
        chargeOffsetZ = 0.1;
        chargeRotY = -20;
        chargeRotX = -20;
      }
    }

    // Sprint 5 — bob state machine. Idle / walk / run / crouch curves
    // mirror the spec amplitudes (3 mm idle, 5 mm walk, 10 mm vertical +
    // 4 mm horizontal run, 0.5× idle crouch).
    const move = this.movementProbe?.getMovementState();
    let bobFreq = 3.14;   // ≈0.5 Hz × 2π
    let bobAmp = 0.003;   // 3 mm
    let bobHorizAmp = 0;
    if (move?.airborne) {
      bobFreq = 0;
      bobAmp = 0;
    } else if (move?.crouching && move.moving) {
      bobFreq = 4;
      bobAmp = 0.0015; // idle × 0.5 while crouch-moving
    } else if (move?.moving) {
      if (move.walking) {
        bobFreq = 6.28; // ≈1 Hz
        bobAmp = 0.005;
        bobHorizAmp = 0.001;
      } else {
        bobFreq = 12.57; // ≈2 Hz
        bobAmp = 0.010;
        bobHorizAmp = 0.004;
      }
    } else if (move?.crouching) {
      // Stationary crouch — half idle bob.
      bobFreq = 3.14;
      bobAmp = 0.0015;
    }
    const bobY = Math.sin(this.phase * bobFreq) * bobAmp;
    const bobX = Math.sin(this.phase * bobFreq * 0.5) * bobHorizAmp;

    this.root.setLocalPosition(
      0.2 + knifeOffsetX + reloadOffsetX + inspectOffsetX + chargeOffsetX + bobX,
      -0.16 + bobY + switchOffset + knifeOffsetY + reloadOffsetY + inspectOffsetY + chargeOffsetY,
      -0.46 + kickOffset + knifeOffsetZ + reloadOffsetZ + inspectOffsetZ + chargeOffsetZ,
    );
    this.root.setLocalEulerAngles(
      -3 + switchRotX + kickRotX + knifeRotX + reloadRotX + inspectRotX + chargeRotX + molotovRotX,
      5 + knifeRotY + reloadRotY + inspectRotY + chargeRotY,
      switchRotZ + kickRotZ + knifeRotZ + reloadRotZ + inspectRotZ + chargeRotZ + molotovRotZ,
    );

    // Drive the muzzle flash decay every frame: scale + intensity ramp
    // out smoothly instead of a hard binary on/off. Curve: brightest at
    // t=0, ~30% at half-life, dark at end. Outer puff lags slightly.
    if (this.muzzleFlash && this.muzzleFlash.enabled) {
      const elapsed = performance.now() - this.muzzleFlashStart;
      const t = elapsed / this.MUZZLE_DURATION_MS;
      if (t >= 1) {
        this.muzzleFlash.enabled = false;
      } else {
        // ease-out cubic for the bright stab, slower ease for puff
        const coreFade = Math.pow(1 - t, 2.4);
        const puffFade = Math.pow(1 - t, 1.4);
        const lightComp = this.muzzleFlash.light;
        if (lightComp) lightComp.intensity = 3.2 * coreFade;
        if (this.muzzleCore) {
          const s = 0.05 + 0.10 * coreFade; // shrink as it fades
          this.muzzleCore.setLocalScale(s, s, s);
        }
        if (this.muzzlePuff) {
          const s = 0.18 + 0.22 * puffFade; // grows then fades
          this.muzzlePuff.setLocalScale(s, s * 0.7, s);
          // tiny rotation per frame for "smoke shimmer"
          const r = this.muzzlePuff.getLocalEulerAngles();
          this.muzzlePuff.setLocalEulerAngles(r.x, r.y, r.z + _dt * 240);
        }
      }
    }
  };

  constructor(app: pc.AppBase, camera: pc.Entity, client: GameClient, movementProbe: MovementProbe | null = null) {
    this.app = app;
    this.client = client;
    this.movementProbe = movementProbe;
    this.root = new pc.Entity("local-weapon-vm");
    this.root.setLocalPosition(0.2, -0.16, -0.46);
    this.root.setLocalEulerAngles(-3, 5, 0);
    camera.addChild(this.root);

    const refresh = () => {
      const sid = this.client.sessionId;
      if (!sid) return;
      const self = this.client.getLatestSelf();
      const id = self?.weaponId ?? "g18";
      
      if (id !== this.nextWeaponId) {
        this.nextWeaponId = id;
        if (!this.lastWeaponId) {
          this.lastWeaponId = id;
          this.rebuild(id);
        } else {
          this.isSwitching = true;
          this.hasSwappedMesh = false;
          if (this.switchPhase > this.SWITCH_DURATION) {
            this.switchPhase = this.SWITCH_DURATION - (this.switchPhase - this.SWITCH_DURATION);
          } else if (this.switchPhase === 0 || this.switchPhase >= this.SWITCH_DURATION * 2) {
            this.switchPhase = 0;
          }
        }
      }

      if (self?.reloading && !this.isReloading) {
        this.isReloading = true;
        this.reloadPhase = 0;
        try {
          const spec = getWeapon(id);
          this.reloadDuration = spec.reloadTime;
        } catch {
          this.reloadDuration = 2.0;
        }
      } else if (!self?.reloading && this.isReloading) {
        this.isReloading = false;
        // Reset parts
        if (this.magEntity) this.magEntity.setLocalPosition(this.magOriginalPos);
        if (this.slideEntity) {
          this.slideEntity.setLocalPosition(this.slideOriginalPos);
          if (this.lastWeaponId === "awp" || this.lastWeaponId === "sniper") {
            this.slideEntity.setLocalEulerAngles(0, 0, 0);
          }
        }
      }
    };

    refresh();
    this.unsubs.push(
      this.client.onPlayerChange((_, sessionId) => {
        if (sessionId === this.client.sessionId) refresh();
      }),
    );
    this.unsubs.push(
      this.client.onWeaponEquip((msg) => {
        if (msg.sessionId === this.client.sessionId) refresh();
      }),
    );
    // Muzzle flash on every authoritative-frame fire (throttled by RPM).
    this.unsubs.push(this.client.onLocalFire(() => this.flashMuzzle()));

    // Knife attack animation
    this.unsubs.push(this.client.onLocalKnifeAttack((type) => {
      if (this.lastWeaponId !== "knife") return;
      this.isKnifeAttacking = true;
      this.knifeAttackPhase = 0;
      this.knifeAttackType = type;
    }));

    // Sprint 5 — F key triggers the inspect animation (CS:GO style).
    const onInspectKey = (e: KeyboardEvent) => {
      if (e.code !== "KeyF") return;
      if (e.repeat) return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      if (document.body.classList.contains("buy-open")) return;
      if (document.body.classList.contains("chat-open")) return;
      // Block if a higher-priority anim is running so the player can't
      // queue inspect into the middle of reload / switch / knife.
      if (this.isReloading || this.isSwitching || this.isKnifeAttacking) return;
      this.isInspecting = true;
      this.inspectPhase = 0;
      e.preventDefault();
    };
    window.addEventListener("keydown", onInspectKey, { capture: true });
    this.unsubs.push(() => window.removeEventListener("keydown", onInspectKey, { capture: true } as any));

    this.ensureMuzzleFlash();
    this.app.on("update", this.onAppUpdate);
  }

  /**
   * Sprint 5 — true while a view-model animation should suppress fire
   * input (weapon switch + inspect). Knife slashes & reloads keep their
   * own input gates, so they're intentionally excluded here.
   */
  isBusy(): boolean {
    return this.isSwitching || this.isInspecting;
  }

  /** Register the "is the local player scoped?" predicate (AWP). */
  setScopeProbe(fn: (() => boolean) | null): void {
    this.scopeProbe = fn;
  }

  /** Set the grenade probe to read charge state. */
  setGrenadeProbe(fn: (() => { charging: boolean; amount: number }) | null): void {
    this.grenadeProbe = fn;
  }

  /** Lazy-create the muzzle-flash entity once and re-use across rebuilds. */
  private ensureMuzzleFlash(): void {
    if (this.muzzleFlash) return;
    const flash = new pc.Entity("muzzle-flash");
    // Brighter, warmer omni light — pops the surroundings on each shot.
    flash.addComponent("light", {
      type: "omni",
      color: new pc.Color(1.0, 0.72, 0.32),
      intensity: 0,
      range: 2.4,
      castShadows: false,
    });

    // Bright, tight inner core — the "spark" of the muzzle flash.
    const core = new pc.Entity("muzzle-flash-core");
    core.addComponent("render", { type: "sphere" });
    core.setLocalScale(0.15, 0.15, 0.15);
    const coreMat = new pc.StandardMaterial();
    coreMat.diffuse = new pc.Color(0, 0, 0);
    coreMat.emissive = new pc.Color(1.0, 0.92, 0.65);
    coreMat.emissiveIntensity = 4.0;
    coreMat.useLighting = false;
    coreMat.blendType = pc.BLEND_ADDITIVE;
    coreMat.depthWrite = false;
    coreMat.update();
    const cr = core.render;
    if (cr) {
      cr.material = coreMat;
      cr.castShadows = false;
      cr.receiveShadows = false;
    }
    flash.addChild(core);
    this.muzzleCore = core;

    // Soft outer puff — flattened sphere so it reads more like a fireball
    // than a marble. Rotates per frame for a subtle shimmer.
    const puff = new pc.Entity("muzzle-flash-puff");
    puff.addComponent("render", { type: "sphere" });
    puff.setLocalScale(0.4, 0.28, 0.4);
    const puffMat = new pc.StandardMaterial();
    puffMat.diffuse = new pc.Color(0, 0, 0);
    puffMat.emissive = new pc.Color(1.0, 0.55, 0.18);
    puffMat.emissiveIntensity = 2.2;
    puffMat.useLighting = false;
    puffMat.blendType = pc.BLEND_ADDITIVE;
    puffMat.depthWrite = false;
    puffMat.opacity = 0.65;
    puffMat.update();
    const pr = puff.render;
    if (pr) {
      pr.material = puffMat;
      pr.castShadows = false;
      pr.receiveShadows = false;
    }
    flash.addChild(puff);
    this.muzzlePuff = puff;

    flash.setLocalPosition(0, 0.04, -0.42); // tip of an average rifle barrel
    flash.enabled = false;
    this.root.addChild(flash);
    this.muzzleFlash = flash;
  }

  /** Trigger a muzzle flash; throttled to weapon RPM so auto sprays don't saturate. */
  private flashMuzzle(): void {
    let spec;
    try {
      spec = getWeapon(this.lastWeaponId || "g18");
    } catch {
      return;
    }
    if (spec.magSize === 0) return; // no flash for melee/grenades
    const rpm = spec.rpm > 0 ? spec.rpm : 60;
    const minGap = (60_000 / rpm) * 0.85;
    const now = performance.now();
    if (now - this.lastMuzzleMs < minGap) return;
    this.lastMuzzleMs = now;

    // Set kickback profile based on weapon class or specific ID
    this.isKickingBack = true;
    this.kickbackPhase = 0;
    
    if (spec.id === "usp_s") {
      this.currentKickbackProfile = { offset: 0.03, rotX: 5, rotZ: 1 };
      this.kickbackDuration = 0.15;
    } else if (spec.id === "deagle") {
      this.currentKickbackProfile = { offset: 0.08, rotX: 18, rotZ: 4 };
      this.kickbackDuration = 0.35;
    } else if (spec.id === "dualies") {
      // Alternate kickback direction for dualies? We can just use a fast, erratic kickback
      const sign = Math.random() > 0.5 ? 1 : -1;
      this.currentKickbackProfile = { offset: 0.03, rotX: 6, rotZ: 3 * sign };
      this.kickbackDuration = 0.12;
    } else {
      switch (spec.class) {
        case "pistol":
          this.currentKickbackProfile = { offset: 0.04, rotX: 8, rotZ: 2 };
          this.kickbackDuration = 0.18;
          break;
        case "smg":
          this.currentKickbackProfile = { offset: 0.03, rotX: 4, rotZ: 1 };
          this.kickbackDuration = 0.12;
          break;
        case "rifle":
          this.currentKickbackProfile = { offset: 0.06, rotX: 6, rotZ: -2 };
          this.kickbackDuration = 0.15;
          break;
        case "sniper":
          this.currentKickbackProfile = { offset: 0.12, rotX: 15, rotZ: 5 };
          this.kickbackDuration = 0.40;
          break;
        default:
          this.currentKickbackProfile = { offset: 0.04, rotX: 6, rotZ: 1 };
          this.kickbackDuration = 0.15;
      }
    }

    // Spawn Tracer
    let isLeftDualie = false;
    if (spec.id === "dualies") {
      // @ts-ignore - dynamic property
      this.dualieToggle = !this.dualieToggle;
      // @ts-ignore
      isLeftDualie = this.dualieToggle;
    }
    this.spawnTracer(spec, isLeftDualie);

    if (!this.muzzleFlash) this.ensureMuzzleFlash();
    if (!this.muzzleFlash) return;

    // Adjust muzzle flash position based on weapon
    if (spec.id === "usp_s") {
      this.muzzleFlash.setLocalPosition(0, 0.025, -0.42);
    } else if (spec.id === "deagle") {
      this.muzzleFlash.setLocalPosition(0, 0.03, -0.30);
    } else if (spec.id === "dualies") {
      this.muzzleFlash.setLocalPosition(isLeftDualie ? -0.15 : 0.15, 0.025, -0.23);
    } else if (spec.id === "mac10") {
      this.muzzleFlash.setLocalPosition(0, 0.04, -0.20);
    } else if (spec.id === "mp9") {
      this.muzzleFlash.setLocalPosition(0, -0.01, -0.21);
    } else if (spec.class === "pistol") {
      this.muzzleFlash.setLocalPosition(0, 0.025, -0.25);
    } else if (spec.class === "smg") {
      this.muzzleFlash.setLocalPosition(0, 0.01, -0.46);
    } else if (spec.class === "sniper") {
      this.muzzleFlash.setLocalPosition(0, 0.03, -0.85);
    } else {
      this.muzzleFlash.setLocalPosition(0, 0.04, -0.48); // rifle
    }

    // Randomize roll on each shot so consecutive flashes don't look identical.
    const roll = Math.random() * 360;
    this.muzzleFlash.setLocalEulerAngles(0, 0, roll);

    this.muzzleFlash.enabled = true;
    this.muzzleFlashStart = now;
    // The decay (scale + intensity) is driven inside onAppUpdate.
  }

    private spawnTracer(spec: any, isLeftDualie: boolean = false): void {
    // In CS:GO, local tracers are very fast, thin, and start from the muzzle.
    // We don't want them spawning behind the camera or looking like huge white rectangles.
    const tracer = new pc.Entity("tracer");
    tracer.addComponent("render", { type: "cylinder" });
    
    let length = 2.0;
    let thickness = 0.008;
    let color = new pc.Color(1.0, 0.8, 0.4);
    let speed = 350; // m/s (visual only, very fast)
    let forwardOffset = 0.8; // Distance from camera to muzzle

    if (spec.id === "usp_s") {
      length = 1.2; thickness = 0.005; color = new pc.Color(0.8, 0.8, 1.0); speed = 250; forwardOffset = 0.7;
    } else if (spec.id === "deagle") {
      length = 2.5; thickness = 0.012; color = new pc.Color(1.0, 0.6, 0.2); speed = 400; forwardOffset = 0.8;
    } else if (spec.id === "dualies") {
      length = 1.5; thickness = 0.006; color = new pc.Color(1.0, 0.9, 0.5); speed = 300; forwardOffset = 0.7;
    } else if (spec.id === "mac10") {
      length = 1.8; thickness = 0.007; color = new pc.Color(1.0, 0.8, 0.4); speed = 320; forwardOffset = 0.55;
    } else if (spec.id === "mp9") {
      length = 1.8; thickness = 0.007; color = new pc.Color(1.0, 0.8, 0.4); speed = 320; forwardOffset = 0.55;
    } else {
      switch (spec.class) {
        case "pistol":
          length = 1.5; thickness = 0.006; color = new pc.Color(1.0, 0.9, 0.6); speed = 300; forwardOffset = 0.7;
          break;
        case "smg":
          length = 2.0; thickness = 0.008; color = new pc.Color(1.0, 0.7, 0.3); speed = 350; forwardOffset = 0.75;
          break;
        case "rifle":
          length = 3.0; thickness = 0.01; color = new pc.Color(1.0, 0.6, 0.2); speed = 450; forwardOffset = 0.9;
          break;
        case "sniper":
          length = 5.0; thickness = 0.015; color = new pc.Color(1.0, 0.4, 0.1); speed = 600; forwardOffset = 1.2;
          break;
      }
    }

    tracer.setLocalScale(thickness, length, thickness);
    
    const mat = new pc.StandardMaterial();
    mat.diffuse = new pc.Color(0, 0, 0);
    mat.emissive = color;
    mat.emissiveIntensity = 8; // Brighter but thinner
    mat.useLighting = false;
    mat.blendType = pc.BLEND_ADDITIVE;
    mat.depthWrite = false;
    mat.update();
    tracer.render!.material = mat;
    tracer.render!.castShadows = false;
    tracer.render!.receiveShadows = false;

    const cam = this.root.parent; // Camera entity
    if (!cam) return;
    
    const startPos = cam.getPosition().clone();
    const forward = cam.forward.clone();
    const right = cam.right.clone();
    const up = cam.up.clone();
    
    let rightOffset = 0.15;
    if (spec.id === "dualies") {
      rightOffset = isLeftDualie ? -0.15 : 0.15;
    } else if (spec.class === "rifle" || spec.class === "sniper") {
      rightOffset = 0.1;
    }
    
    // Position the center of the cylinder such that the BACK of the cylinder is at the muzzle.
    // The cylinder is 'length' long, so its center is length/2 ahead of the muzzle.
    const centerOffset = forwardOffset + (length / 2);
    
    startPos.add(right.mulScalar(rightOffset)).add(up.mulScalar(-0.1)).add(forward.clone().mulScalar(centerOffset));

    tracer.setPosition(startPos);
    
    tracer.lookAt(startPos.clone().add(forward));
    tracer.rotateLocal(90, 0, 0);

    this.app.root.addChild(tracer);

    const DURATION = 0.15; // Much shorter lifetime since it's faster
    let age = 0;
    
    const onUpdate = (dt: number) => {
      age += dt;
      if (age > DURATION) {
        this.app.off("update", onUpdate);
        tracer.destroy();
        return;
      }
      
      const p = tracer.getPosition();
      p.add(forward.clone().mulScalar(speed * dt));
      tracer.setPosition(p);
      
      // Fade out smoothly
      mat.opacity = Math.max(0, 1 - (age / DURATION));
      mat.update();
    };
    
    this.app.on("update", onUpdate);
  }

  destroy(): void {
    this.app.off("update", this.onAppUpdate);
    for (const u of this.unsubs) u();
    this.root.destroy();
  }

  private clearMeshes(): void {
    for (let i = this.root.children.length - 1; i >= 0; i--) {
      const child = this.root.children[i];
      // Preserve the persistent muzzle-flash entity through rebuilds.
      if (child === this.muzzleFlash) continue;
      child.destroy();
    }
  }

  private mat(r: number, g: number, b: number, gloss = 0.35): pc.StandardMaterial {
    const m = new pc.StandardMaterial();
    m.diffuse = new pc.Color(r, g, b);
    m.gloss = gloss;
    m.update();
    return m;
  }

  private addPart(
    parent: pc.Entity,
    type: "box" | "cylinder" | "capsule" | "sphere",
    name: string,
    scale: [number, number, number],
    pos: [number, number, number],
    euler: [number, number, number],
    mat: pc.StandardMaterial,
  ): pc.Entity {
    const e = new pc.Entity(name);
    e.addComponent("render", { type });
    e.setLocalScale(scale[0], scale[1], scale[2]);
    e.setLocalPosition(pos[0], pos[1], pos[2]);
    e.setLocalEulerAngles(euler[0], euler[1], euler[2]);
    const render = e.render;
    if (render) {
      render.material = mat;
      render.castShadows = false;
      render.receiveShadows = false;
    }
    parent.addChild(e);
    return e;
  }

  private rebuild(weaponId: string): void {
    this.clearMeshes();

    // Mesh construction lives in weaponBuilder.ts (shared with the
    // ground-item renderer so a dropped AK is literally the same mesh
    // as the one in your hands). We pass `refs` so the builder can hand
    // back the slide + magazine entities — those drive reload / slide-
    // pull animations, which the in-hand view-model owns.
    const refs: WeaponBuildRefs = {};
    buildWeaponModel(this.root, weaponId, refs);
    this.slideEntity = refs.slideEntity ?? null;
    this.magEntity = refs.magEntity ?? null;
    if (refs.slideOriginalPos) this.slideOriginalPos.copy(refs.slideOriginalPos);
    if (refs.magOriginalPos) this.magOriginalPos.copy(refs.magOriginalPos);

    if (weaponId === "molotov") {
      this.startMolotovLighting();
    }
  }

  private startMolotovLighting(): void {
    this.isLightingMolotov = true;
    this.lightMolotovPhase = 0;
    this.molotovFlameEntities = [];
    
    // Find flame parts and hide them
    const flameNames = [
      "molotov-flame-base", "molotov-flame-outer", "molotov-flame-mid",
      "molotov-flame-hot", "molotov-flame-core", "molotov-flame-tongue",
      "molotov-flame-wisp", "molotov-smoke-cap", "molotov-wick-ember"
    ];
    for (const child of this.root.children) {
      if (flameNames.includes(child.name)) {
        child.enabled = false;
        this.molotovFlameEntities.push(child as pc.Entity);
      }
    }

    // Build the Zippo lighter
    this.lighterEntity = new pc.Entity("zippo-lighter");
    this.root.addChild(this.lighterEntity);
    
    // Initial position: down and left (hidden)
    this.lighterEntity.setLocalPosition(-0.3, -0.2, 0.1);
    
    const lighterMat = this.mat(0.7, 0.75, 0.8, 0.8); // Silver metal
    const lighterDark = this.mat(0.1, 0.1, 0.1, 0.3);
    
    // Body
    this.addPart(this.lighterEntity, "box", "zippo-body", [0.03, 0.04, 0.015], [0, 0, 0], [0, 0, 0], lighterMat);
    // Hinge
    this.addPart(this.lighterEntity, "cylinder", "zippo-hinge", [0.004, 0.015, 0.004], [-0.015, 0.02, 0], [0, 0, 0], lighterDark);
    // Inner chimney
    this.addPart(this.lighterEntity, "box", "zippo-chimney", [0.025, 0.015, 0.012], [0.002, 0.025, 0], [0, 0, 0], lighterMat);
    // Wheel
    this.addPart(this.lighterEntity, "cylinder", "zippo-wheel", [0.008, 0.004, 0.008], [-0.008, 0.025, 0], [90, 0, 0], lighterDark);
    
    // Lid (hinged at the left)
    this.lighterLidEntity = new pc.Entity("zippo-lid-pivot");
    this.lighterEntity.addChild(this.lighterLidEntity);
    this.lighterLidEntity.setLocalPosition(-0.015, 0.02, 0); // pivot at hinge
    
    this.addPart(this.lighterLidEntity, "box", "zippo-lid", [0.03, 0.015, 0.015], [0.015, 0.0075, 0], [0, 0, 0], lighterMat);
  }


}

